import React from "react";
import Highlighter from "react-highlight-words";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useDispatch, useSelector } from "src/store";
import { CustomTileLink } from "src/ui/shared/CustomTile";
import { CheckboxField } from "src/ui/shared/form/CheckboxField";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";
import { makeStyles } from "tss-react/mui";

import {
    addBatchSelection,
    removeBatchSelection,
    selectBatchActionSelectionTransformed,
    selectSearchQuery,
    showDetails,
} from "@dashboard/devices/store";
import type { CheckboxProps } from "@mui/material";
import { Theme, useTheme } from "@mui/material";
import TimeService from "@services/timeService/timeService";
import { Tooltip } from "@shared/Tooltip";
const useStyles = makeStyles()((muiTheme: Theme) => ({
    licenseSummaryIndicator: {
        width: muiTheme.spacing(1),
        borderRadius: "24px",
        height: muiTheme.spacing(8),
    },
    statusCell: {
        display: "flex",
        alignItems: "center",
        padding: muiTheme.spacing(1, 2),
        cursor: "pointer",
    },
    selectAllLicensesCheckbox: {
        width: "50px",
        minWidth: "50px",
        maxWidth: "50px",
    },
}));

type TableCellProps = {
    value: string | null;
    style?: React.CSSProperties;
    onClick?: () => void;
};

const TableCell = React.memo(({ value, style, onClick }: TableCellProps) => {
    const searchQuery = useSelector(selectSearchQuery);
    const theme = useTheme();

    return (
        <MinimalTableCell {...{ style, onClick }}>
            {value ? (
                <Highlighter
                    searchWords={[searchQuery]}
                    textToHighlight={value}
                    autoEscape
                    unhighlightStyle={{ color: theme.palette.emteria.text.primary }}
                />
            ) : null}
        </MinimalTableCell>
    );
});
TableCell.displayName = "TableCell";

const TableCellWithTooltip = React.memo(({ value, style, title, onClick }: TableCellProps & { title?: string | null }) => {
    const searchQuery = useSelector(selectSearchQuery);
    const theme = useTheme();

    return (
        <MinimalTableCell {...{ style, onClick }}>
            <Tooltip
                title={title || value || ""}
                placement="top"
                PopperProps={{ disablePortal: false }}
                disableHoverListener={!value}
            >
                <Highlighter
                    searchWords={[searchQuery]}
                    textToHighlight={value || ""}
                    unhighlightStyle={{ color: theme.palette.emteria.text.primary }}
                    autoEscape
                />
            </Tooltip>
        </MinimalTableCell>
    );
});
TableCellWithTooltip.displayName = "TableCellWithTooltip";

const MoveLicenseCell = (props: { value: number; id: string }) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const selectBatchActionSelected = useSelector(selectBatchActionSelectionTransformed);
    const checked = props.value in selectBatchActionSelected;

    const onChange: NonNullable<CheckboxProps["onChange"]> = (event) => {
        const fn = event.target.checked ? addBatchSelection : removeBatchSelection;
        dispatch(fn(props.value));
    };

    return (
        <MinimalTableCell data-column-id={props.id} className={classes.selectAllLicensesCheckbox}>
            <CheckboxField {...{ checked, onChange }} />
        </MinimalTableCell>
    );
};

const StatusCell = React.memo((props: { value: { color: string; tooltip: string } }) => {
    const { classes } = useStyles();

    return (
        <MinimalTableCell className={classes.statusCell}>
            <Tooltip placement="right" title={props.value.tooltip}>
                <div className={classes.licenseSummaryIndicator} style={{ backgroundColor: props.value.color }} />
            </Tooltip>
        </MinimalTableCell>
    );
});
StatusCell.displayName = "StatusCell";

const GroupCell = ({ value, id }: { value: string; id: number }) => {
    const { t } = useTranslation(["common"]);
    const dispatch = useDispatch();
    const searchQuery = useSelector(selectSearchQuery);

    return (
        <MinimalTableCell data-column-id="groupName">
            <Tooltip title={t("common:tooltip.showGroupDetails")}>
                <CustomTileLink
                    onClick={() => {
                        dispatch(
                            showDetails({
                                type: "group",
                                selectedId: id,
                            }),
                        );
                    }}
                >
                    <Highlighter searchWords={[searchQuery]} textToHighlight={value} autoEscape />
                </CustomTileLink>
            </Tooltip>
        </MinimalTableCell>
    );
};

const LastActivityCell = ({ value }: { value: string | null }) => {
    return (
        <TableCellWithTooltip
            value={value ? TimeService.getHumanReadableTimeDifference(value, { zone: "UTC", withAffix: true }) : null}
            title={value ? TimeService.getLocalDate(value, "ISO", "dateTime") : null}
        />
    );
};

const NameCell = React.memo(({ value }: { value: string }) => {
    const searchQuery = useSelector(selectSearchQuery);

    return (
        <MinimalTableCell data-column-id="name">
            <Tooltip title={i18next.t("pages:devicePage.tooltip.showDeviceInfo")}>
                <CustomTileLink>
                    <Highlighter searchWords={[searchQuery]} textToHighlight={value} autoEscape />
                </CustomTileLink>
            </Tooltip>
        </MinimalTableCell>
    );
});
NameCell.displayName = "NameCell";

const WarningsCell = React.memo(
    ({ value, warningsList }: { value: string; warningsList: ("lowMemory" | "lowBattery" | "nonCompliant")[] }) => {
        const searchQuery = useSelector(selectSearchQuery);
        const theme = useTheme();
        const translatedValue =
            value === "OK" ? i18next.t("pages:devicePage.table.health.ok") : i18next.t("pages:devicePage.table.health.warning");

        if (warningsList.length > 0) {
            const tooltipContent = (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {warningsList.map((warning, index) => (
                        <React.Fragment key={index}>
                            {warning === "lowMemory" && <li>{i18next.t("pages:overviewPage.cards.deviceWarnings.lowMemory")}</li>}
                            {warning === "lowBattery" && (
                                <li>{i18next.t("pages:overviewPage.cards.deviceWarnings.lowBattery")}</li>
                            )}
                            {warning === "nonCompliant" && (
                                <li>{i18next.t("pages:overviewPage.cards.deviceWarnings.nonCompliant")}</li>
                            )}
                        </React.Fragment>
                    ))}
                </ul>
            );
            return (
                <MinimalTableCell>
                    <Tooltip title={tooltipContent} placement="top">
                        <Highlighter
                            searchWords={[searchQuery]}
                            textToHighlight={translatedValue}
                            autoEscape
                            unhighlightStyle={{ color: theme.palette.emteria.text.primary }}
                        />
                    </Tooltip>
                </MinimalTableCell>
            );
        }
        return (
            <MinimalTableCell>
                <Highlighter
                    searchWords={[searchQuery]}
                    textToHighlight={translatedValue}
                    autoEscape
                    unhighlightStyle={{ color: theme.palette.emteria.text.primary }}
                />
            </MinimalTableCell>
        );
    },
);
WarningsCell.displayName = "WarningsCell";

export { GroupCell, LastActivityCell, MoveLicenseCell, NameCell, StatusCell, TableCell, TableCellWithTooltip, WarningsCell };
